"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { usePathname } from "next/navigation";
import { useStore } from "@/app/context/StoreContext";
import type { ViewConfig } from "@/lib/views";

type DataStoreScope = {
  storeScope: NonNullable<ViewConfig["storeScope"]>;
  selectedStoreIds: string[];
};

export type DataScopeOverride = Pick<ViewConfig, "storeScope" | "selectedStoreIds">;

type DataScopeContextValue = {
  scopeQuery: string;
  viewScopeQuery: string;
  scopeReady: boolean;
  setViewStoreScope: (view: Pick<ViewConfig, "storeScope" | "selectedStoreIds"> | null) => void;
};

const DataScopeContext = createContext<DataScopeContextValue | null>(null);

type DataScopeState = {
  scope: DataStoreScope | null;
  routeKey: string;
  scopeReady: boolean;
};

type PendingRouteDetail = {
  view?: Pick<ViewConfig, "storeScope" | "selectedStoreIds"> | null;
  pathname?: string;
};

function needsViewScopedData(pathname: string | null | undefined): boolean {
  return !!pathname && (pathname.startsWith("/view/") || pathname.startsWith("/templates/preview/"));
}

function viewScopeToQuery(scope: DataStoreScope | null): string {
  if (!scope || scope.storeScope === "current") return "";
  if (scope.storeScope === "all") return "?stores=all";
  if (scope.selectedStoreIds.length === 0) return "";
  return `?stores=${encodeURIComponent(scope.selectedStoreIds.join(","))}`;
}

function currentStoreToQuery(mode: "single" | "all", storeId: string | null): string {
  if (mode === "all") return "?stores=all";
  if (!storeId) return "";
  return `?stores=${encodeURIComponent(storeId)}`;
}

function scopeToQuery(scope: DataStoreScope | null, current: { mode: "single" | "all"; storeId: string | null }): string {
  if (!scope || scope.storeScope === "current") return currentStoreToQuery(current.mode, current.storeId);
  return viewScopeToQuery(scope);
}

function viewToScope(view: Pick<ViewConfig, "storeScope" | "selectedStoreIds">): DataStoreScope {
  return {
    storeScope: view.storeScope ?? "current",
    selectedStoreIds: [...new Set(view.selectedStoreIds ?? [])],
  };
}

export function viewToScopeQuery(view: Pick<ViewConfig, "storeScope" | "selectedStoreIds"> | null): string {
  if (!view) return "";
  return viewScopeToQuery(viewToScope(view));
}

export { withDataScope } from "@/lib/data-scope-query";

export function DataScopeProvider({ children, scopeOverride }: { children: ReactNode; scopeOverride?: DataScopeOverride }) {
  const pathname = usePathname();
  const { store, mode, loading } = useStore();
  const routeKey = pathname ?? "";
  const routeNeedsScope = needsViewScopedData(pathname);
  const overriddenScope = useMemo(() => scopeOverride ? viewToScope(scopeOverride) : null, [scopeOverride]);
  const hasScopeOverride = scopeOverride !== undefined;
  const [state, setState] = useState<DataScopeState>(() => ({
    scope: null,
    routeKey,
    scopeReady: !routeNeedsScope,
  }));
  const setViewStoreScope = useCallback((view: Pick<ViewConfig, "storeScope" | "selectedStoreIds"> | null) => {
    if (!view) {
      setState({ scope: null, routeKey, scopeReady: !needsViewScopedData(routeKey) });
      return;
    }
    setState({
      scope: viewToScope(view),
      routeKey,
      scopeReady: true,
    });
  }, [routeKey]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<PendingRouteDetail>).detail;
      const pendingPathname = detail?.pathname ?? "";

      if (!needsViewScopedData(pendingPathname)) {
        setState({ scope: null, routeKey: pendingPathname, scopeReady: true });
        return;
      }

      if (!detail?.view) {
        setState({ scope: null, routeKey: pendingPathname, scopeReady: false });
        return;
      }

      setState({
        scope: viewToScope(detail.view),
        routeKey: pendingPathname,
        scopeReady: true,
      });
    };

    window.addEventListener("frogstat:route-pending", handler);
    return () => window.removeEventListener("frogstat:route-pending", handler);
  }, []);

  const viewScopeReady = hasScopeOverride
    ? true
    : routeNeedsScope ? state.scopeReady && state.routeKey === routeKey : true;
  // Явный скоуп страницы уже содержит всё необходимое для запроса. Не ждём,
  // пока общий StoreProvider догонит сервер после перехода между страницами.
  const scopeReady = hasScopeOverride ? true : viewScopeReady && !loading;
  /**
   * Скоуп страницы вида берём всегда, а на остальных маршрутах — только если
   * экран сам его задал (setViewStoreScope) и задал ИМЕННО для этого маршрута.
   *
   * Раньше здесь стоял жёсткий `routeNeedsScope ? state.scope : null`, и любой
   * экран вне /view молча оставался на активном магазине. Из-за этого вкладки
   * магазина на «Склейках» переключались, а данные — нет. Проверка routeKey
   * оставлена, чтобы скоуп, заданный на прошлом экране, не утекал на следующий.
   */
  const effectiveScope = hasScopeOverride
    ? overriddenScope
    : routeNeedsScope || state.routeKey === routeKey ? state.scope : null;
  const currentStoreId = store?.id ?? null;
  const viewScopeQuery = useMemo(() => viewScopeReady ? viewScopeToQuery(effectiveScope) : "", [viewScopeReady, effectiveScope]);
  const scopeQuery = useMemo(
    () => scopeReady ? scopeToQuery(effectiveScope, { mode, storeId: currentStoreId }) : "",
    [scopeReady, effectiveScope, mode, currentStoreId],
  );
  const value = useMemo(() => ({ scopeQuery, viewScopeQuery, scopeReady, setViewStoreScope }), [scopeQuery, viewScopeQuery, scopeReady, setViewStoreScope]);
  return <DataScopeContext.Provider value={value}>{children}</DataScopeContext.Provider>;
}

export function useDataScope() {
  const ctx = useContext(DataScopeContext);
  if (!ctx) return { scopeQuery: "", viewScopeQuery: "", scopeReady: true, setViewStoreScope: () => {} };
  return ctx;
}
